import { create } from 'zustand';
import {
  createAgentStatusEventSource,
  loadTiaAgentStatus,
  type AgentStatusResponse,
} from '../services/opennessAgent';
import { streamingPollDelayMs } from '../utils/streamingPollBackoff';

export type TiaAgentStatusLoadState = 'detecting' | 'ready' | 'stale' | 'error';

interface TiaAgentStatusState {
  status: AgentStatusResponse | null;
  loadState: TiaAgentStatusLoadState;
  error: string | null;
  lastUpdatedAt: number | null;
  contextualProjectId: string | null;
  refresh: (projectId?: string | null) => Promise<void>;
  start: () => () => void;
}

let source: EventSource | null = null;
/** An open is in flight; a second one would mint a ticket it throws away. */
let opening = false;
let pollTimer: number | null = null;
let pollAttempts = 0;
let reconnectTimer: number | null = null;
let reconnectAttempts = 0;
let subscribers = 0;
let requestSequence = 0;

interface InFlightRefresh {
  key: string;
  promise: Promise<void>;
  /** A push arrived while this request was out: ask again once it settles (refreshAfterPush). */
  again: boolean;
}

let inFlight: InFlightRefresh | null = null;

/** Listeners notified when an agent transitions from offline/absent to online */
type AgentOnlineListener = () => void;
const onlineListeners = new Set<AgentOnlineListener>();

/**
 * Subscribe to agent-online transitions. Returns an unsubscribe function.
 * Fires when the connectionState changes from anything other than 'online'
 * to 'online' (including from null/undefined → 'online' on first detection).
 */
export function onAgentCameOnline(listener: AgentOnlineListener): () => void {
  onlineListeners.add(listener);
  return () => {
    onlineListeners.delete(listener);
  };
}

/**
 * The status stream's word that a validation wrote a conversation's TIA
 * project reference outside a turn, a project it created (E11 ③; the
 * gateway's session_tia_target_written): which conversation, and the
 * snapshot's revision when the gateway read it.
 */
export interface SessionTiaTargetWritten {
  sessionId: string;
  runId?: string;
  sessionConfigRevision?: number;
}

type SessionTiaTargetWrittenListener = (written: SessionTiaTargetWritten) => void;
const sessionTiaTargetListeners = new Set<SessionTiaTargetWrittenListener>();

/**
 * Subscribe to session_tia_target_written (T11). Returns an unsubscribe
 * function. It arrives only while the stream is open, which a component
 * holding the feed keeps it (start).
 */
export function onSessionTiaTargetWritten(listener: SessionTiaTargetWrittenListener): () => void {
  sessionTiaTargetListeners.add(listener);
  return () => {
    sessionTiaTargetListeners.delete(listener);
  };
}

/** A stream message's data when it is session_tia_target_written; undefined for any other. */
function readSessionTiaTargetWritten(data: unknown): SessionTiaTargetWritten | undefined {
  if (typeof data !== 'string') return undefined;
  let message: unknown;
  try {
    message = JSON.parse(data);
  } catch {
    return undefined;
  }
  if (!message || typeof message !== 'object') return undefined;
  const { type, sessionId, runId, sessionConfigRevision } = message as Record<string, unknown>;
  if (type !== 'session_tia_target_written' || typeof sessionId !== 'string' || !sessionId) return undefined;
  return {
    sessionId,
    ...(typeof runId === 'string' ? { runId } : {}),
    ...(typeof sessionConfigRevision === 'number' && Number.isFinite(sessionConfigRevision) ? { sessionConfigRevision } : {}),
  };
}

/**
 * Fold concurrent refreshes for the same project into one request.
 *
 * The composer, both agent settings dialogs and the post-generation prompt all
 * mount this feed and all ask for a refresh. One request per consumer per
 * render pass was enough to trip the gateway's own rate limiter, and the 429s
 * came back as "Failed to refresh streaming sessions" with nothing on screen.
 */
function refreshStatus(projectId?: string | null): Promise<void> {
  if (projectId !== undefined) {
    useTiaAgentStatusStore.setState({ contextualProjectId: projectId });
  }
  const effectiveProjectId = projectId === undefined
    ? useTiaAgentStatusStore.getState().contextualProjectId
    : projectId;
  const key = effectiveProjectId ?? '';

  if (inFlight?.key === key) return inFlight.promise;

  const entry: InFlightRefresh = {
    key,
    again: false,
    promise: loadStatusFor(effectiveProjectId).finally(() => {
      if (inFlight === entry) inFlight = null;
      // Once more for a push that arrived meanwhile, for the same project,
      // unless the page has moved to another one since.
      if (entry.again && (useTiaAgentStatusStore.getState().contextualProjectId ?? '') === key) {
        void refreshStatus(effectiveProjectId);
      }
    }),
  };
  inFlight = entry;
  return entry.promise;
}

/**
 * A push on the status stream says the status changed. Folded into a request
 * already out, it took that request's answer, which may predate the change,
 * and with the stream open nothing polls to correct it: the button stayed
 * stale until the next push (T4 review). The request is marked instead, and
 * asked again once it settles. Refreshes the page asks for itself still fold
 * into one request.
 */
function refreshAfterPush(): void {
  const key = useTiaAgentStatusStore.getState().contextualProjectId ?? '';
  if (inFlight?.key === key) {
    inFlight.again = true;
    return;
  }
  void refreshStatus();
}

/**
 * A message on the status stream. Every other one says an Agent's status
 * changed (refreshAfterPush). session_tia_target_written says a
 * conversation's project reference did, and nothing of any Agent, so it goes
 * to its listeners without a status request.
 */
function onStreamMessage(event?: MessageEvent): void {
  const written = readSessionTiaTargetWritten(event?.data);
  if (!written) {
    refreshAfterPush();
    return;
  }
  sessionTiaTargetListeners.forEach((fn) => { try { fn(written); } catch { /* ignore listener errors */ } });
}

async function loadStatusFor(effectiveProjectId: string | null): Promise<void> {
  const previous = useTiaAgentStatusStore.getState().status;
  const sequence = ++requestSequence;
  if (!previous) {
    useTiaAgentStatusStore.setState({ loadState: 'detecting', error: null });
  }
  await loadTiaAgentStatus(effectiveProjectId)
    .then((status) => {
      if (sequence !== requestSequence) return;
      useTiaAgentStatusStore.setState({
        status,
        loadState: 'ready',
        error: null,
        lastUpdatedAt: Date.now(),
      });
      // Notify listeners on offline→online transition
      const newConnectionState = status.connectionState ?? status.status;
      const prevConnectionState = previous?.connectionState ?? previous?.status;
      if (prevConnectionState !== 'online' && newConnectionState === 'online') {
        onlineListeners.forEach((fn) => { try { fn(); } catch { /* ignore listener errors */ } });
      }
    })
    .catch((error: unknown) => {
      if (sequence !== requestSequence) return;
      useTiaAgentStatusStore.setState({
        // Never turn a transient HTTP/SSE failure into a false disconnect.
        status: previous,
        loadState: previous ? 'stale' : 'error',
        error: error instanceof Error ? error.message : 'RealPLC Agent status unavailable',
      });
    });
}

/**
 * Poll only while no stream is open: 3 s after the drop, then doubling to a
 * 30 s ceiling (streamingPollDelayMs), as chained timeouts. The stream's
 * 'open' stops it. A flat 15 s interval used to run beside a healthy stream
 * for the whole session; with several components mounting the feed it was
 * most of what the status endpoint saw, and it fed the gateway's rate limiter.
 */
function startPolling(): void {
  if (typeof window === 'undefined' || pollTimer !== null) return;
  pollTimer = window.setTimeout(() => {
    pollTimer = null;
    if (subscribers === 0) return;
    void refreshStatus();
    pollAttempts += 1;
    startPolling();
  }, streamingPollDelayMs(pollAttempts));
}

function stopPolling(): void {
  if (pollTimer !== null) window.clearTimeout(pollTimer);
  pollTimer = null;
  pollAttempts = 0;
}

function openStatusStream(): void {
  // Opening is async: the factory first exchanges the session for a single-use
  // stream ticket. Two things can happen while that is in flight, and both used
  // to leave a stream open that nobody held.
  //
  // The subscriber count can reach zero — already handled below. And the count
  // can bounce 1 -> 0 -> 1, which is ordinary when a component remounts: the
  // teardown ran `source?.close()` while `source` was still null, closing
  // nothing, and this function was entered a second time. Both opens then
  // resolved, the second overwrote `source`, and the first stayed connected with
  // no reference to it until the browser gave up on it. That is the
  // net::ERR_ABORTED seen three times in the 2026-09-07 walkthrough, each behind
  // a freshly minted ticket, before one finally stuck.
  if (opening) return;
  opening = true;

  void createAgentStatusEventSource()
    .then((opened) => {
      opening = false;
      if (subscribers === 0 || source) {
        // Nobody wants it, or somebody else's stream already holds the slot.
        opened?.close();
        return;
      }
      source = opened;
      source?.addEventListener('open', () => { reconnectAttempts = 0; stopPolling(); });
      source?.addEventListener('message', onStreamMessage);
      source?.addEventListener('error', () => {
        source?.close();
        source = null;
        const current = useTiaAgentStatusStore.getState();
        if (current.status) useTiaAgentStatusStore.setState({ loadState: 'stale' });
        scheduleStreamReconnect();
        startPolling();
      });
    })
    .catch(() => {
      opening = false;
      // A ticket that cannot be minted is the same situation as a dropped
      // stream: back off and try again rather than falling silent.
      scheduleStreamReconnect();
      startPolling();
    });
}

/**
 * Reopen the stream after it drops.
 *
 * Without this the stream dies on its first error and never comes back, so the
 * fallback poll becomes the only source of status for the rest of the session — and
 * with several components mounting the feed, that poll was enough to trip the
 * gateway's own rate limiter.
 */
function scheduleStreamReconnect(): void {
  if (typeof window === 'undefined') return;
  if (subscribers === 0 || reconnectTimer !== null) return;
  const delay = Math.min(60_000, 2_000 * 2 ** reconnectAttempts);
  reconnectAttempts += 1;
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    if (subscribers > 0) openStatusStream();
  }, delay);
}

function startGlobalStatusFeed(): () => void {
  subscribers += 1;
  if (subscribers === 1 && typeof window !== 'undefined') {
    void refreshStatus();
    openStatusStream();
    // The fallback until the stream is confirmed open; its 'open' stops it.
    startPolling();
  }

  return () => {
    subscribers = Math.max(0, subscribers - 1);
    if (subscribers > 0) return;
    source?.close();
    source = null;
    stopPolling();
    if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
    reconnectAttempts = 0;
  };
}

export const useTiaAgentStatusStore = create<TiaAgentStatusState>(() => ({
  status: null,
  loadState: 'detecting',
  error: null,
  lastUpdatedAt: null,
  contextualProjectId: null,
  refresh: refreshStatus,
  start: startGlobalStatusFeed,
}));
